import { AxiosResponse } from "axios";
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    Tab,
    Tabs,
    TextField,
    Typography
} from "@mui/material";
import { Box, Stack, styled } from "@mui/system";
import React, { useCallback, useEffect, useState } from "react";
import { useLogin } from "providers/Login";
import { LoadingButton, TabContext, TabPanel } from "@mui/lab";
import { useNotification } from "providers/Notification";
import { CloudDownload, CloudUpload } from "@mui/icons-material";
import DataTable from "components/Table";
import { formatDateTime, fromUnix } from "helpers/dateFormat";

interface ExportLogListState {
    count: number
    items: ExportLog[]
}

interface ExportLog {
    id: string
    type: number
    timestamp: number
    description: string
    fileName: string
    nullafiVersion: string
    exportId: string
    exportVersion: string
    exportTimestamp: number
    fileExists: boolean
}

function ImportExport() {
    const notification = useNotification();
    const login = useLogin();
    const [logsLoading, setLogsLoading] = useState(false);

    const [importEncryptionKey, setImportEncryptionKey] = useState("");
    const [importLoading, setImportLoading] = useState(false);
    const [importFile, setImportFile] = useState<any>(undefined);

    const [exportEncryptionkey, setExportEncryptionKey] = useState("");
    const [exportDescription, setExportDescription] = useState("");
    const [exportLoading, setExportLoading] = useState(false);
    const [exportLogListState, setExportLogListState] = useState<ExportLogListState>({
        count: 0,
        items: []
    });

    const [takeCount, setTakeCount] = useState(25);
    const [skipItems, setSkipItems] = useState(0);


    const [importDescription, setImportDescription] = useState("");

    const [importDataTypes, setImportDataTypes] = useState(false);
    const [importMaskFormats, setImportMaskFormats] = useState(false);
    const [importApps, setImportApps] = useState(false);
    const [importObfuscations, setImportObfuscations] = useState(false);
    const [importRules, setImportRules] = useState(false);
    const [importAlerts, setImportAlerts] = useState(false);
    const [importActivityFilters, setImportActivityFilters] = useState(false);
    const [importApiKeys, setImportApiKeys] = useState(false);
    const [importGroups, setImportGroups] = useState(false);
    const [importGeneralConfig, setImportGeneralConfig] = useState(false);
    const [importICAPConfig, setImportICAPConfig] = useState(false);
    const [importBypassConfig, setImportBypassConfig] = useState(false);
    const [importAuthenticationConfig, setImportAuthenticationConfig] = useState(false);
    const [importDirectoryIntegrationConfig, setImportDirectoryIntegrationConfig] = useState(false);
    const [importNotificationsConfig, setImportNotificationsConfig] = useState(false);

    const allPolicyChecked = importDataTypes && importMaskFormats && importApps && importObfuscations && importRules && importAlerts && importActivityFilters && importApiKeys && importGroups;
    const somePolicyChecked = importDataTypes || importMaskFormats || importApps || importObfuscations || importRules || importAlerts || importActivityFilters || importApiKeys || importGroups;
    const allConfigChecked = importGeneralConfig && importICAPConfig && importBypassConfig && importAuthenticationConfig && importDirectoryIntegrationConfig && importNotificationsConfig;
    const someConfigChecked = importGeneralConfig || importICAPConfig || importBypassConfig || importAuthenticationConfig || importDirectoryIntegrationConfig || importNotificationsConfig;

    const togglePolicyCheckboxes = (value: boolean) => {
        setImportDataTypes(value);
        setImportMaskFormats(value);
        setImportApps(value);
        setImportObfuscations(value);
        setImportRules(value);
        setImportAlerts(value);
        setImportActivityFilters(value);
        setImportApiKeys(value);
        setImportGroups(value);
    };

    const toggleConfigCheckboxes = (value: boolean) => {
        setImportGeneralConfig(value);
        setImportICAPConfig(value);
        setImportBypassConfig(value);
        setImportAuthenticationConfig(value);
        setImportDirectoryIntegrationConfig(value);
        setImportNotificationsConfig(value);
    };

    const [currentTab, setCurrentTab] = useState("export");

    const req = login.GetAxios();

    const loadData = useCallback(async () => {
        if (!req) {
            return; //not logged in, nothing to do
        }
        try {
            setLogsLoading(true);
            const resp = await req.get("/api/config/exportlogs", {
                params: {
                    take: takeCount,
                    skip: skipItems,
                } 
            });

            setExportLogListState({
                count: resp.data.count || 0,
                items: resp.data.items || []
            });
        } catch (err) {
            notification.Display({
                title: "Error",
                message: "Error fetching configuration data",
                type: "error",
            });
        } finally {
            setLogsLoading(false);
        }
    }, [notification, req, skipItems, takeCount]);

    const downloadResponse = (response: AxiosResponse<any, any>) => {
        const contentDisposition = response.headers["content-disposition"];
        let fileName = "export.zip"; // fallback filename

        if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (fileNameMatch && fileNameMatch[1]) {
                fileName = fileNameMatch[1];
            }
        }

        const contentType = response.headers["content-type"];
        const blob = new Blob([response.data], { type: typeof contentType === "string" ? contentType : undefined });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const downloadExportFile = async () => {
        if (!req) {
            return; //not logged in, nothing to do
        }

        if (exportEncryptionkey.length >= 6 && exportDescription) {
            try {

                setExportLoading(true);

                const response = await req.post("/api/config/export", {
                    encryptionKey: exportEncryptionkey,
                    description: exportDescription
                }, {
                    responseType: "blob"
                });

                downloadResponse(response);
                setExportEncryptionKey("");
                setExportDescription("");
            } finally {
                setExportLoading(false);
                loadData();
            }
        }
    };

    const downloadExportFileById = async (exportLogId: string) => {
        if (!req) {
            return; //not logged in, nothing to do
        }

        try {
            const response = await req.post(`/api/config/exportlogs/${exportLogId}/download`, {
                encryptionKey: exportEncryptionkey,
                description: exportDescription
            }, {
                responseType: "blob"
            });

            downloadResponse(response);
        } catch {
            notification.Display({
                title: "Error",
                message: "Could not download this file. File not found.",
                type: "error",
            });
        }
    };

    const readImportFile = async (event: any) => {
        if (!req) {
            return; //not logged in, nothing to do
        }


        if (!event.target.files || event.target.files.length === 0) {
            return;
        }

        const importFile = event.target.files[0];

        if (!importFile) {
            alert("Please select a ZIP file before submitting.");
            return;
        }

        setImportLoading(true);
        setImportFile(undefined);
        setImportDescription("");
        toggleCheckboxes(false);

        try {
            const formData = new FormData();
            formData.append("exportFile", importFile);
            formData.append("encryptionKey", importEncryptionKey);

            // Replace the URL below with your actual backend endpoint
            const response = await req.post("/api/config/import/info", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.data && response.data.metadata) {
                setImportDescription(response.data.metadata.description);
            }

            setImportFile(importFile);
            event.target.value = null;
        } catch (error) {
            notification.Display({
                title: "Import File Upload",
                message: "Error trying to read import file. Please make sure you set up the correct password and the corect file.",
                type: "error",
            });
        } finally {
            setImportLoading(false);
        }
    };

    const importExportFile = async () => {
        if (!req) {
            return; //not logged in, nothing to do
        }


        if (!importFile) {
            return;
        }

        setImportLoading(true);

        try {
            const formData = new FormData();
            formData.append("exportFile", importFile);
            formData.append("encryptionKey", importEncryptionKey);

            if (allPolicyChecked && allConfigChecked) {
                formData.append("fullRestore", "true");
            }

            if (importDataTypes) {
                formData.append("includeDataTypes", "true");
            }

            if (importMaskFormats) {
                formData.append("includeMaskFormats", "true");
            }

            if (importApps) {
                formData.append("includeApplications", "true");
            }

            if (importObfuscations) {
                formData.append("includeObfuscations", "true");
            }

            if (importRules) {
                formData.append("includeRules", "true");
            }

            if (importAlerts) {
                formData.append("includeAlerts", "true");
            }

            if (importActivityFilters) {
                formData.append("includeActivityFilters", "true");
            }

            if (importGroups) {
                formData.append("includeGroups", "true");
            }

            if (importApiKeys) {
                formData.append("includeAPIKeys", "true");
            }

            if (importGeneralConfig) {
                formData.append("includeGeneralConfig", "true");
            }

            if (importICAPConfig) {
                formData.append("includeICAPConfig", "true");
            }

            if (importBypassConfig) {
                formData.append("includeBypassConfig", "true");
            }

            if (importAuthenticationConfig) {
                formData.append("includeAuthenticationConfig", "true");
            }

            if (importDirectoryIntegrationConfig) {
                formData.append("includeDirectoryIntegrationConfig", "true");
            }

            if (importNotificationsConfig) {
                formData.append("includeNotificationsConfig", "true");
            }

            // Replace the URL below with your actual backend endpoint
            await req.post("/api/config/import", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            setImportFile(undefined);
            setImportEncryptionKey("");
            toggleCheckboxes(false);
            setImportDescription("");
            notification.Display({
                title: "Import File Upload",
                message: "File imported succesfully !",
                type: "success",
            });
        } catch (error) {
            notification.Display({
                title: "Import File Upload",
                message: "Error trying to read import file. Please make sure you set up the correct password and the corect file.",
                type: "error",
            });
        } finally {
            setImportLoading(false);
            loadData();
        }
    };

    const cancelImport = () => {
        setImportFile(undefined);
        setImportEncryptionKey("");
        setImportDescription("");
        toggleCheckboxes(false);
    };

    const toggleCheckboxes = (value: boolean) => {
        setImportDataTypes(value);
        setImportMaskFormats(value);
        setImportObfuscations(value);
        setImportApps(value);
        setImportRules(value);
        setImportAlerts(value);
        setImportActivityFilters(value);
        setImportApiKeys(value);
        setImportGeneralConfig(value);
        setImportICAPConfig(value);
        setImportBypassConfig(value);
        setImportAuthenticationConfig(value);
        setImportDirectoryIntegrationConfig(value);
        setImportNotificationsConfig(value);
        setImportGroups(value);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipItems, takeCount]);

    useEffect(() => {
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const hasSomethingToImport = importDataTypes || importMaskFormats || importObfuscations || importApps ||
        importRules || importAlerts || importActivityFilters || importApiKeys ||
        importGeneralConfig || importICAPConfig || importBypassConfig || importAuthenticationConfig ||
        importDirectoryIntegrationConfig || importNotificationsConfig || importGroups;

    return <>
        <Typography variant="h1">Configuration Import/Export</Typography>
        <Card sx={{ marginTop: "10px", padding: "0px" }}>
            <Box sx={{ flexGrow: 1, bgcolor: "background.paper", display: "flex" }}>
                <TabContext value={currentTab}>
                    <Tabs
                        orientation="vertical"
                        value={currentTab}
                        sx={{ borderRight: 1, borderColor: "divider" }}
                        onChange={(e, value) => setCurrentTab(value)}
                    >
                        <Tab
                            icon={<CloudDownload />}
                            label="Export"
                            value="export"
                            id="export"
                        ></Tab>
                        <Tab
                            icon={<CloudUpload />}
                            label="Import"
                            value="import"
                            id="import"
                        ></Tab>
                    </Tabs>
                    <TabPanel value="export" sx={{ width: "100%" }}>
                        <Typography variant="h2">Export</Typography>
                        <Typography variant="body2" color="text.secondary" marginBottom={"20px"}>Please create a strong encryption passphrase to export your data and make sure to save that info securely.</Typography>
                        <Divider />
                        <Grid container>
                            <Grid item sm={12} marginTop={"20px"}>
                                <Stack gap={2} justifyContent={"start"} alignItems={"start"}>
                                    <TextField fullWidth sx={{ flexGrow: 1, maxWidth: 400 }} size="small" label="Encryption passphrase" name="export-encryption" type="password" autoComplete="one-time-code" value={exportEncryptionkey} onChange={(e) => setExportEncryptionKey(e.currentTarget.value)} />
                                    <TextField fullWidth sx={{ flexGrow: 1, maxWidth: 600 }} size="small" label="Description" value={exportDescription} onChange={(e) => setExportDescription(e.currentTarget.value)} />
                                    <LoadingButton variant="contained" color="primary" loading={exportLoading} disabled={Boolean(exportEncryptionkey.length < 6 || !exportDescription)} onClick={downloadExportFile} startIcon={<CloudDownload />}>Download</LoadingButton>
                                </Stack>
                            </Grid>
                        </Grid>
                    </TabPanel>
                    <TabPanel value="import" sx={{ width: "100%" }}>
                        <Typography variant="h2">Import</Typography>
                        <Typography variant="body2" color="text.secondary" marginBottom={"20px"}>Please upload the exported file you want to import your data from. Make sure to select the data you want to import.</Typography>
                        <Divider />
                        <Grid container gap={2} rowGap={2} marginTop={"20px"}>
                            <Grid item sm={12} >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row",
                                    gap: 2
                                }}>
                                    <LoadingButton
                                        component="label"
                                        role={undefined}
                                        loading={importLoading}
                                        variant="contained"
                                        tabIndex={-1}
                                        startIcon={<CloudUpload />}
                                    >
                                        Upload
                                        <VisuallyHiddenInput
                                            type="file"
                                            accept=".zip"
                                            onChange={readImportFile}
                                        />
                                    </LoadingButton>
                                    <Stack direction="row" gap={1} alignItems={"end"}>
                                        <Typography visibility={importFile ? "visible" : "hidden"} variant="h3">Description: </Typography>
                                        <Typography variant="body1">{importDescription}</Typography>
                                    </Stack>
                                </Box>

                            </Grid>
                            <Grid container sm={12} padding={"0 20px"}>
                                {/* Policy Section */}
                                <Grid item sm={12}>
                                    <FormControlLabel
                                        label={<Typography variant="h4">Policy</Typography>}
                                        disabled={!importFile}
                                        control={
                                            <Checkbox
                                                checked={allPolicyChecked}
                                                indeterminate={somePolicyChecked && !allPolicyChecked}
                                                onChange={() => togglePolicyCheckboxes(!allPolicyChecked)}
                                            />
                                        }
                                    />
                                </Grid>
                                <Grid container sm={12} sx={{ pl: 4 }}>
                                    <Grid item sm={3}>
                                        <Stack>
                                            <FormControlLabel
                                                label={"Data Types"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importDataTypes} onChange={() => setImportDataTypes(!importDataTypes)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"Mask Formats"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importMaskFormats} onChange={() => setImportMaskFormats(!importMaskFormats)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"Applications"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importApps} onChange={() => setImportApps(!importApps)} />
                                                }
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item sm={3}>
                                        <Stack>
                                            <FormControlLabel
                                                label={"Obfuscations"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importObfuscations} onChange={() => setImportObfuscations(!importObfuscations)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"Rules"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importRules} onChange={() => setImportRules(!importRules)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"Alerts"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importAlerts} onChange={() => setImportAlerts(!importAlerts)} />
                                                }
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item sm={3}>
                                        <Stack>
                                            <FormControlLabel
                                                label={"Activity Filters"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importActivityFilters} onChange={() => setImportActivityFilters(!importActivityFilters)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"API Keys"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importApiKeys} onChange={() => setImportApiKeys(!importApiKeys)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"Groups"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importGroups} onChange={() => setImportGroups(!importGroups)} />
                                                }
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>

                                <Grid item sm={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>

                                {/* Configuration Section */}
                                <Grid item sm={12}>
                                    <FormControlLabel
                                        label={<Typography variant="h4">Configuration</Typography>}
                                        disabled={!importFile}
                                        control={
                                            <Checkbox
                                                checked={allConfigChecked}
                                                indeterminate={someConfigChecked && !allConfigChecked}
                                                onChange={() => toggleConfigCheckboxes(!allConfigChecked)}
                                            />
                                        }
                                    />
                                </Grid>
                                <Grid container sm={12} sx={{ pl: 4 }}>
                                    <Grid item sm={3}>
                                        <Stack>
                                            <FormControlLabel
                                                label={"General Config"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importGeneralConfig} onChange={() => setImportGeneralConfig(!importGeneralConfig)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"ICAP Config"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importICAPConfig} onChange={() => setImportICAPConfig(!importICAPConfig)} />
                                                }
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item sm={3}>
                                        <Stack>
                                            <FormControlLabel
                                                label={"Bypass Config"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importBypassConfig} onChange={() => setImportBypassConfig(!importBypassConfig)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"Authentication Config"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importAuthenticationConfig} onChange={() => setImportAuthenticationConfig(!importAuthenticationConfig)} />
                                                }
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item sm={3}>
                                        <Stack>
                                            <FormControlLabel
                                                label={"Directory Integration Config"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importDirectoryIntegrationConfig} onChange={() => setImportDirectoryIntegrationConfig(!importDirectoryIntegrationConfig)} />
                                                }
                                            />
                                            <FormControlLabel
                                                label={"Notification Settings"}
                                                disabled={!importFile}
                                                control={
                                                    <Checkbox checked={importNotificationsConfig} onChange={() => setImportNotificationsConfig(!importNotificationsConfig)} />
                                                }
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item sm={12}>
                                <Stack direction={"row"} justifyContent={"start"} gap={2}>
                                    <TextField disabled={!importFile || !hasSomethingToImport} fullWidth sx={{ flexGrow: 1, maxWidth: 400 }} size="small" label="Encryption passphrase" name="import-encryption" type="password" autoComplete="one-time-code" value={importEncryptionKey} onChange={(e) => setImportEncryptionKey(e.currentTarget.value)} />
                                    <Button variant="contained" disabled={!importFile || !hasSomethingToImport || importEncryptionKey.length < 6} onClick={importExportFile}>Confirm</Button>
                                    <Button variant="outlined" disabled={!importFile} onClick={cancelImport}>Cancel</Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </TabContext>
            </Box>
        </Card>
        <Box display="flex" flexDirection="column">
            <Card sx={{ marginTop: "30px", padding: "0px" }}>
                <CardHeader component={Typography} titleTypographyProps={{ variant: "h3" }} title={"Logs"} />
                <CardContent sx={{ padding: "10px" }}>
                    <DataTable
                        size="small"
                        sx={{ marginTop: "5px" }}
                        data={exportLogListState.items}
                        dataCount={exportLogListState.count}
                        paginationStartItem={skipItems}
                        paginationItemCount={takeCount}
                        onPaginationStartItemChanged={async (x) => {
                            setSkipItems(x);
                            setTakeCount(takeCount);
                        }}
                        onPaginationItemCountChanged={async (x) => {
                            setTakeCount(Number(x));
                        }}
                        columnsDataId="dataGrid_exportLog"
                        columnsState={[
                            {
                                columnId: "timestamp",
                                width: "10%",
                            },
                            {
                                columnId: "type",
                                width: "10%",
                            },
                            {
                                columnId: "description",
                                width: "15%",
                            },
                            {
                                columnId: "nullafiVersion",
                                width: "10%",
                            },
                            {
                                columnId: "exportVersion",
                                width: "10%",
                            },
                            {
                                columnId: "exportTimestamp",
                                width: "10%",
                            },
                            {
                                columnId: "download",
                                width: "10%",
                            },
                        ]}
                        columns={{
                            timestamp: {
                                displayName: "Timestamp",
                                render: (a) => {
                                    return <Typography fontSize="small">{formatDateTime(fromUnix(a.timestamp))}</Typography>;
                                }
                            },
                            type: {
                                displayName: "Type",
                                render: (a) => {
                                    return a.type === 1 ? "Import" : "Export";
                                }
                            },
                            description: {
                                displayName: "Description",
                                render: (a) => {
                                    return a.description;
                                }
                            },
                            nullafiVersion: {
                                displayName: "Nullafi Version",
                                render: (a) => {
                                    return a.nullafiVersion;
                                }
                            },
                            exportVersion: {
                                displayName: "Export Version",
                                render: (a) => {
                                    return a.exportVersion;
                                }
                            },
                            exportTimestamp: {
                                displayName: "Export Timestamp",
                                render: (a) => {
                                    return <Typography fontSize="small">{formatDateTime(fromUnix(a.exportTimestamp))}</Typography>;
                                }
                            },
                            download: {
                                displayName: "",
                                render: (a) => {
                                    return <Button size="small" variant="contained" disabled={!a.fileExists} startIcon={<CloudDownload />} onClick={() => downloadExportFileById(a.id)}>Download</Button>;
                                }
                            },
                        }}
                        finishedLoading={!logsLoading}
                    />
                </CardContent>
            </Card>
        </Box >
    </>;
}

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

export default ImportExport;